import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Paper, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography, Checkbox, FormControlLabel, FormHelperText,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { BankTransferService, TransferAction } from '../../services/bankTransfer.service';
import { PERMISSIONS } from '../../constants/permissions';
import { Transfer, TransferStatus } from '../../interfaces/treasury.interface';
import { BankAccount } from '../../interfaces/bank/bankAccount.interface';
import { Segment } from '../../interfaces/accounting.interface';
import { SearchableSelect } from '../common/SearchableSelect';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import { formatDate, toLocalIsoDate } from '../../utils/dateUtils';
import { formatMoney, humanizeEnum } from '../../utils/format';
import { helpElement } from '../../help/attrs';
import { FILTER_CELL, bankLabel, segmentLabel, useTreasuryRefData } from './treasuryRef';

const STATUSES: TransferStatus[] = ['REQUESTED', 'APPROVED', 'SENT', 'RECEIVED', 'REJECTED', 'CANCELLED'];
const COLORS: Record<TransferStatus, 'default' | 'warning' | 'info' | 'success' | 'error'> = {
  REQUESTED: 'warning', APPROVED: 'info', SENT: 'info', RECEIVED: 'success', REJECTED: 'error', CANCELLED: 'default',
};

type Dialogue =
  | { kind: 'create' }
  | { kind: 'reject' | 'cancel' | 'send' | 'receive' | 'send-and-receive'; t: Transfer };

/** Help keys of the transfer dialog, by kind (literals so the help key scan sees them). */
const TRANSFER_DLG_HELP: Record<Dialogue['kind'], { paper: Record<string, string>; helpKey: string }> = {
  create: { paper: { 'data-help-page': 'bank.transferCreate.page' }, helpKey: 'bank.transferCreate.save' },
  reject: { paper: { 'data-help-page': 'bank.transferReject.page' }, helpKey: 'bank.transferReject.save' },
  cancel: { paper: { 'data-help-page': 'bank.transferCancel.page' }, helpKey: 'bank.transferCancel.save' },
  send: { paper: { 'data-help-page': 'bank.transferSend.page' }, helpKey: 'bank.transferSend.save' },
  receive: { paper: { 'data-help-page': 'bank.transferReceive.page' }, helpKey: 'bank.transferReceive.save' },
  'send-and-receive': { paper: { 'data-help-page': 'bank.transferSendReceive.page' }, helpKey: 'bank.transferSendReceive.save' },
};

/** Help attributes of the Notes / Reason field, by kind. */
const TRANSFER_NOTES_HELP: Record<Dialogue['kind'], Record<string, string>> = {
  create: { 'data-help': 'bank.transferAct.field.notes' },
  reject: { 'data-help': 'bank.transferAct.field.reason' },
  cancel: { 'data-help': 'bank.transferAct.field.notes' },
  send: { 'data-help': 'bank.transferAct.field.notes' },
  receive: { 'data-help': 'bank.transferAct.field.notes' },
  'send-and-receive': { 'data-help': 'bank.transferAct.field.notes' },
};

const BankTransfers: React.FC = () => {
  const toast = useToast();
  const { hasPermission } = useAuth();
  const canManage = hasPermission(PERMISSIONS.BANK_ACCOUNT_MANAGE);
  const canApprove = hasPermission(PERMISSIONS.JOURNAL_ENTRY_POST);
  const institutionId = AuthService.getSelectedInstitution()!;
  const ref = useTreasuryRefData(e => toast.showApiError(e, 'Failed to load reference data'));
  const [rows, setRows] = useState<Transfer[]>([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [dlg, setDlg] = useState<Dialogue | null>(null);
  const [saving, setSaving] = useState(false);

  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [amount, setAmount] = useState('');
  const [charges, setCharges] = useState('');
  const [chargesFromAmount, setChargesFromAmount] = useState(false);
  const [chargesAccountId, setChargesAccountId] = useState('');
  const [segmentId, setSegmentId] = useState('');
  const [requestDate, setRequestDate] = useState(toLocalIsoDate());
  const [valueDate, setValueDate] = useState('');
  const [reference, setReference] = useState('');
  const [remarks, setRemarks] = useState('');
  const [date, setDate] = useState(toLocalIsoDate());
  const [notes, setNotes] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setRows(await BankTransferService.list(institutionId, status || undefined));
    } catch (e) {
      toast.showApiError(e, 'Failed to load transfers');
    } finally {
      setLoading(false);
    }
  }, [institutionId, status]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const openDlg = (d: Dialogue) => {
    setDlg(d); setNotes(''); setDate(toLocalIsoDate());
    if (d.kind === 'create') {
      setFrom(''); setTo(''); setAmount(''); setCharges(''); setChargesFromAmount(false); setChargesAccountId(''); setSegmentId('');
      setRequestDate(toLocalIsoDate()); setValueDate(''); setReference(''); setRemarks('');
    }
  };

  const hasCharges = Number(charges) > 0;
  const valid = (() => {
    if (!dlg) return false;
    if (dlg.kind === 'create') {
      return !!from && !!to && from !== to && Number(amount) > 0 && (!hasCharges || !!chargesAccountId);
    }
    if (dlg.kind === 'reject') return notes.trim().length > 0;
    return true;
  })();

  const save = async () => {
    if (!dlg) return;
    try {
      setSaving(true);
      if (dlg.kind === 'create') {
        await BankTransferService.create({
          institutionId, fromBankAccountId: from, toBankAccountId: to, amount: Number(amount),
          charges: hasCharges ? Number(charges) : undefined, chargesFromAmount, chargesAccountId: hasCharges ? chargesAccountId : undefined,
          segmentId: segmentId || undefined, requestDate: requestDate || undefined, valueDate: valueDate || undefined,
          reference: reference.trim() || undefined, remarks: remarks.trim() || undefined,
        });
      } else {
        const act: TransferAction = dlg.kind;
        await BankTransferService.act(dlg.t.id, act, { date: date || undefined, notes: notes.trim() || undefined });
      }
      toast.showSuccess('Saved');
      setDlg(null);
      await load();
    } catch (e) {
      toast.showApiError(e, 'Transfer action failed');
    } finally {
      setSaving(false);
    }
  };

  const approve = async (t: Transfer) => {
    try {
      await BankTransferService.approve(t.id);
      toast.showSuccess('Approved');
      await load();
    } catch (e) {
      toast.showApiError(e, 'Approval failed');
    }
  };

  const titles: Record<string, string> = {
    create: 'New bank transfer', reject: 'Reject transfer', cancel: 'Cancel transfer', send: 'Mark transfer sent',
    receive: 'Mark transfer received', 'send-and-receive': 'Send and receive',
  };

  return (
    <Box data-help-page="banking.transfers.page" sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5">Bank transfers</Typography>
        {canManage && <Button data-help="banking.transfers.new" variant="contained" startIcon={<AddIcon />} onClick={() => openDlg({ kind: 'create' })}>New transfer</Button>}
      </Box>
      <Box data-help-section="banking.transfers.filter" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <TextField data-help="banking.transfers.statusFilter" select label="Status" value={status} onChange={e => setStatus(e.target.value)} sx={FILTER_CELL}>
          <MenuItem value="">All</MenuItem>
          {STATUSES.map(s => <MenuItem key={s} value={s}>{humanizeEnum(s)}</MenuItem>)}
        </TextField>
      </Box>
      {loading ? <CircularProgress /> : (
        <TableContainer data-help-section="banking.transfers.table" component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell data-help="banking.transfers.col.number">No.</TableCell><TableCell data-help="banking.transfers.col.from">From</TableCell><TableCell data-help="banking.transfers.col.to">To</TableCell>
                <TableCell data-help="banking.transfers.col.amount" align="right">Amount</TableCell><TableCell data-help="banking.transfers.col.requested">Requested</TableCell><TableCell data-help="banking.transfers.col.status">Status</TableCell>
                <TableCell data-help="banking.transfers.col.actions">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(t => (
                <TableRow key={t.id} hover>
                  <TableCell>{t.transferNumber}</TableCell>
                  <TableCell>{t.fromLabel}</TableCell>
                  <TableCell>{t.toLabel}</TableCell>
                  <TableCell align="right">
                    {formatMoney(t.amount)}
                    {(t.charges ?? 0) > 0 && (
                      <Typography variant="caption" display="block" color="text.secondary" data-testid={`charges-mode-${t.id}`}>
                        {t.chargesFromAmount ? 'Charges deducted from amount received' : 'Charges borne by sender on top'}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{t.requestDate ? formatDate(t.requestDate) : '—'}</TableCell>
                  <TableCell>
                    <Chip size="small" label={humanizeEnum(t.status)} color={COLORS[t.status]} />
                    {t.rejectedReason && <Typography variant="caption" display="block">{t.rejectedReason}</Typography>}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {t.status === 'REQUESTED' && canApprove && (
                        <>
                          <Button data-help="banking.transfers.action.approve" size="small" onClick={() => approve(t)}>Approve</Button>
                          <Button data-help="banking.transfers.action.reject" size="small" color="error" onClick={() => openDlg({ kind: 'reject', t })}>Reject</Button>
                        </>
                      )}
                      {t.status === 'APPROVED' && canManage && (
                        <>
                          <Button data-help="banking.transfers.action.send" size="small" onClick={() => openDlg({ kind: 'send', t })}>Send</Button>
                          <Button data-help="banking.transfers.action.sendReceive" size="small" onClick={() => openDlg({ kind: 'send-and-receive', t })}>Send and receive</Button>
                        </>
                      )}
                      {t.status === 'SENT' && canManage && (
                        <Button data-help="banking.transfers.action.receive" size="small" onClick={() => openDlg({ kind: 'receive', t })}>Receive</Button>
                      )}
                      {(t.status === 'REQUESTED' || t.status === 'APPROVED') && canManage && (
                        <Button data-help="banking.transfers.action.cancel" size="small" color="warning" onClick={() => openDlg({ kind: 'cancel', t })}>Cancel</Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && <TableRow><TableCell colSpan={7} align="center">No transfers.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={!!dlg} onClose={() => setDlg(null)} maxWidth="sm" fullWidth PaperProps={dlg ? TRANSFER_DLG_HELP[dlg.kind].paper as any : undefined}>
        <DialogTitle>{dlg ? titles[dlg.kind] : ''}{dlg && dlg.kind !== 'create' ? ` ${dlg.t.transferNumber}` : ''}</DialogTitle>
        {dlg && (
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }} data-help-section="bank.transferDialog.section.body">
            {dlg.kind === 'create' ? (
              <>
                <SearchableSelect<BankAccount> data-help="bank.transferCreate.field.from" label="From bank account" options={ref.banks.filter(b => !b.currencyCode)} value={from} required
                  getOptionLabel={bankLabel} onChange={id => setFrom(id)} />
                <SearchableSelect<BankAccount> data-help="bank.transferCreate.field.to" label="To bank account" options={ref.banks.filter(b => !b.currencyCode && b.id !== from)} value={to} required
                  getOptionLabel={bankLabel} onChange={id => setTo(id)} />
                <TextField data-help="bank.transferCreate.field.amount" label="Amount" type="number" value={amount} required fullWidth onChange={e => setAmount(e.target.value)} />
                <TextField data-help="bank.transferCreate.field.charges" label="Bank charges (optional)" type="number" value={charges} fullWidth onChange={e => setCharges(e.target.value)} />
                {hasCharges && (
                  <>
                    <FormControlLabel data-help="bank.transferCreate.field.chargesFromAmount" label="Charges are deducted from the amount received"
                      control={<Checkbox checked={chargesFromAmount} onChange={e => setChargesFromAmount(e.target.checked)} />} />
                    <FormHelperText sx={{ mt: -1.5 }}>
                      Off (default): the sending account pays the amount plus the charges, and the receiving account gets the full amount.
                      On: the sending account pays only the amount and the receiving account gets the amount less the charges.
                    </FormHelperText>
                  </>
                )}
                {hasCharges && (
                  <AccountAutocomplete data-help="bank.transferCreate.field.chargesAccount" accounts={ref.expenses} value={chargesAccountId} label="Charges expense account" required
                    onChange={id => setChargesAccountId(id)} />
                )}
                <SearchableSelect<Segment> data-help="bank.transferCreate.field.segment" label="Segment" options={ref.segments} value={segmentId} getOptionLabel={segmentLabel}
                  onChange={id => setSegmentId(id)} />
                <TextField data-help="bank.transferCreate.field.requestDate" label="Request date" type="date" value={requestDate} fullWidth InputLabelProps={{ shrink: true }}
                  onChange={e => setRequestDate(e.target.value)} />
                <TextField data-help="bank.transferCreate.field.valueDate" label="Value date" type="date" value={valueDate} fullWidth InputLabelProps={{ shrink: true }}
                  onChange={e => setValueDate(e.target.value)} />
                <TextField data-help="bank.transferCreate.field.reference" label="Reference" value={reference} fullWidth onChange={e => setReference(e.target.value)} />
                <TextField data-help="bank.transferCreate.field.remarks" label="Remarks" value={remarks} fullWidth multiline minRows={2} onChange={e => setRemarks(e.target.value)} />
              </>
            ) : (
              <>
                {(dlg.kind === 'send' || dlg.kind === 'receive' || dlg.kind === 'send-and-receive') && (
                  <TextField data-help="bank.transferAct.field.date" label="Date" type="date" value={date} fullWidth InputLabelProps={{ shrink: true }}
                    onChange={e => setDate(e.target.value)} />
                )}
                <TextField {...TRANSFER_NOTES_HELP[dlg.kind]} label={dlg.kind === 'reject' ? 'Reason' : 'Notes'} value={notes} required={dlg.kind === 'reject'} fullWidth
                  multiline minRows={2} onChange={e => setNotes(e.target.value)} />
              </>
            )}
          </DialogContent>
        )}
        <DialogActions>
          <Button data-help="bank.transfer.dlgClose" onClick={() => setDlg(null)}>Close</Button>
          <Button {...helpElement(dlg ? TRANSFER_DLG_HELP[dlg.kind].helpKey : undefined)} variant="contained" disabled={saving || !valid} onClick={save}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BankTransfers;
